import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguageToggle } from '@/components/ui/LanguageSelector';
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Mail } from 'lucide-react';

/**
 * Public password-reset request page.
 *
 * UX contract: the backend ALWAYS returns 200 with a generic message
 * (anti-enumeration). This page therefore shows the same success view for
 * every outcome (unknown email, Google-only, inactive, nominal) — we never
 * branch based on the response payload.
 */
export function ForgotPasswordPage() {
  const { language } = useLanguage();
  const isFr = language === 'fr';

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const cleanEmail = email.trim();
    if (!cleanEmail) return;

    setIsLoading(true);
    try {
      await authAPI.forgotPassword({ email: cleanEmail });
      setSubmitted(true);
    } catch (err) {
      // 422 (malformed email) is the only case where we surface an error.
      // Any other failure still shows the generic success view to avoid
      // leaking information about which emails are registered.
      if (err.response?.status === 422) {
        setError(isFr ? 'Veuillez saisir une adresse email valide.' : 'Please enter a valid email.');
      } else if (err.response?.status === 429) {
        setError(isFr
          ? 'Trop de tentatives. Veuillez réessayer dans 1 heure.'
          : 'Too many attempts. Please try again in 1 hour.');
      } else {
        // Network error → still show success to avoid enumeration.
        setSubmitted(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary to-background p-4">
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block" data-testid="forgot-logo-link">
            <h1 className="font-heading text-4xl font-black text-foreground tracking-tight">
              Space<span className="text-primary">LeadX</span>
            </h1>
          </Link>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">
              {isFr ? 'Mot de passe oublié ?' : 'Forgot your password?'}
            </CardTitle>
            <CardDescription>
              {isFr
                ? 'Saisissez votre adresse courriel. Si un compte existe, nous vous enverrons un lien de réinitialisation.'
                : 'Enter your email address. If an account exists, we will send you a reset link.'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {submitted ? (
              <div className="space-y-4" data-testid="forgot-success-view">
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    {isFr
                      ? 'Si un compte est éligible avec cette adresse, un lien de réinitialisation vient d\'être envoyé. Vérifiez votre boîte de réception (ainsi que les indésirables).'
                      : 'If an eligible account exists for that email, a reset link has just been sent. Check your inbox (and spam folder).'}
                  </AlertDescription>
                </Alert>
                <Button asChild variant="outline" className="w-full" data-testid="forgot-back-to-login-btn">
                  <Link to="/login">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {isFr ? 'Retour à la connexion' : 'Back to sign in'}
                  </Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
                {error && (
                  <Alert variant="destructive" data-testid="forgot-error-alert">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="forgot-email">{isFr ? 'Adresse courriel' : 'Email address'}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="forgot-email"
                      type="email"
                      autoComplete="email"
                      placeholder="vous@entreprise.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 font-bold"
                  disabled={isLoading}
                  data-testid="forgot-submit-btn"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      {isFr ? 'Envoyer le lien' : 'Send reset link'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <div className="pt-2 text-center">
                  <Link
                    to="/login"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    data-testid="forgot-back-link"
                  >
                    <ArrowLeft className="inline mr-1 h-3 w-3" />
                    {isFr ? 'Retour à la connexion' : 'Back to sign in'}
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
